/**
 * Payout period utility – shared between frontend and backend.
 * Derives fiscal half-year information from dates based on configured payout periods.
 */
import { DateTime, Interval } from 'luxon';
import { payoutPeriods } from '@gbr-monorepo/interfaces';
import { defaultTimezone } from '@gbr-monorepo/interfaces';

type PayoutPeriodName = keyof typeof payoutPeriods;

interface MonthRange {
    start: { month: number };
    end: { month: number };
}

export type FiscalHalf = 'H1' | 'H2';

export interface FiscalHalfYear {
    half: FiscalHalf;
    /** Full calendar year the fiscal year starts in (October). */
    fiscalYearStart: number;
    /** Full calendar year the fiscal year ends in (September). */
    fiscalYearEnd: number;
}

const PAYOUT_PERIOD_HALF: Record<PayoutPeriodName, FiscalHalf> = {
    spring: 'H1',
    winter: 'H2'
};

const FISCAL_YEAR_START_MONTH = payoutPeriods.spring.travelEndDate.start.month;

/**
 * Derives the fiscal year half a date falls into by matching its month to the
 * customer defined `payoutPeriods`. Internally converts to Europe/Berlin so
 * callers can pass raw UTC DateTimes.
 */
export function getFiscalHalfYear(date: DateTime | null): FiscalHalfYear | null {
    if (!date || !date.isValid) {
        return null;
    }

    const local = date.setZone(defaultTimezone);
    const fiscalYearStart = local.month >= FISCAL_YEAR_START_MONTH ? local.year : local.year - 1;

    return {
        half: PAYOUT_PERIOD_HALF[getPayoutPeriodNameForMonth(local.month)],
        fiscalYearStart,
        fiscalYearEnd: fiscalYearStart + 1
    };
}

/**
 * Same as {@link getFiscalHalfYear} but for a payout period option, an ISO 8601
 * interval such as `2025-09-30T22:00:00.000Z/2026-03-31T21:59:59.999Z`, derived
 * from its start date.
 */
export function getFiscalHalfYearFromInterval(interval: string): FiscalHalfYear | null {
    const start = Interval.fromISO(interval).start;
    if (!start || !start.isValid) {
        return null;
    }

    return getFiscalHalfYear(start);
}

/**
 * Formats a FiscalHalfYear as a string like "H1 FY 24/25".
 */
export function formatPayoutPeriod(fiscalHalfYear: FiscalHalfYear | null): string {
    if (!fiscalHalfYear) {
        return '';
    }
    const startYY = `${fiscalHalfYear.fiscalYearStart % 100}`.padStart(2, '0');
    const endYY = `${fiscalHalfYear.fiscalYearEnd % 100}`.padStart(2, '0');
    return `${fiscalHalfYear.half} FY ${startYY}/${endYY}`;
}

function getPayoutPeriodNameForMonth(month: number): PayoutPeriodName {
    const periodNames = Object.keys(payoutPeriods) as PayoutPeriodName[];
    return periodNames.find(name => isMonthInRange(month, payoutPeriods[name].travelEndDate)) ?? periodNames[0];
}

function isMonthInRange(month: number, { start, end }: MonthRange): boolean {
    return start.month <= end.month
        ? month >= start.month && month <= end.month
        : month >= start.month || month <= end.month;
}
